/* global femcare */

femcare.form.circularDatePicker = {
    // Configuration
    radius: 890, // Radius where text is positioned
    circleSize: 1900, // Diameter of the circle
    centerX: 950, // Center X of SVG viewBox
    centerY: 950, // Center Y of SVG viewBox

    // Current state
    isInitialized: false,
    selectedDate: new Date(),
    monthRotation: 0, // Current rotation angle for months wheel (in degrees)
    dayRotation: 0, // Current rotation angle for days wheel (in degrees)

    // Interaction state
    isDragging: false,
    dragTarget: null, // 'months' or 'days'
    startX: 0,
    lastX: 0,
    velocity: 0,
    lastTime: 0,
    animationId: null,

    // DOM elements
    datePickerContainer: null,
    monthsWheel: null,
    daysWheel: null,
    monthsSvg: null,
    daysCircle: null,
    monthTextsGroup: null,
    nativeInput: null,

    // Data
    dayNames: null,
    monthNames: null,

    /**
     * Initializes the circular date picker
     * @param {HTMLElement} container - Optional specific container element to initialize
     */
    init: function (container) {
        // If a specific container is provided, always initialize it (for modal case)
        // Otherwise, prevent multiple initializations
        if (!container && this.isInitialized) {
            return;
        }

        this.datePickerContainer = container || document.querySelector('.circular-date-picker');
        if (!this.datePickerContainer) {
            return;
        }


        this.dayNames = JSON.parse(this.datePickerContainer.getAttribute('data-day-names'));
        this.monthNames = JSON.parse(this.datePickerContainer.getAttribute('data-month-names'));


        this.monthsWheel = this.datePickerContainer.querySelector('.circular-date-picker__wheel--months');
        this.daysWheel = this.datePickerContainer.querySelector('.circular-date-picker__wheel--days');
        this.nativeInput = this.datePickerContainer.querySelector('.circular-date-picker__native-input input[type="date"]');

        if (!this.monthsWheel || !this.daysWheel || !this.nativeInput) {
            return;
        }

        // Get SVG elements for months
        this.monthsSvg = this.monthsWheel.querySelector('.circular-date-picker__svg');
        this.monthTextsGroup = this.monthsSvg.querySelector('#month-texts');

        // Get circle element for days
        this.daysCircle = this.daysWheel.querySelector('.circular-date-picker__circle');

        if (!this.monthsSvg || !this.daysCircle) {
            return;
        }

        // Set initial date to today
        this.selectedDate = new Date();

        // Build SVG content for months and position days
        this.buildMonthsWheel();
        this.positionDaysWheel();
        this.updateNativeInput();

        // Calculate initial rotation to show selected date at top
        this.monthRotation = this.getRotationForMonth(this.selectedDate.getMonth());
        this.dayRotation = this.getRotationForDay(this.selectedDate.getDate() - 1);

        // Apply initial rotation
        this.updateRotation();
        this.updateActiveItems();

        // Bind events
        this.bindEvents();

        // Mark as initialized only if no specific container was provided
        if (!container) {
            this.isInitialized = true;
        }
    },

    /**
     * Creates a circular arc path for SVG textPath
     * @param {number} angle - The center angle of the text (in degrees)
     * @param {number} radius - The radius of the circle
     * @param {number} arcLength - The length of the arc (in degrees)
     * @param {boolean} reverse - Whether the path should go counterclockwise
     * @returns {string} SVG path d attribute
     */
    createArcPath: function (angle, radius, arcLength, reverse) {
        let startAngle = (angle - arcLength / 2 - 90) * Math.PI / 180;
        let endAngle = (angle + arcLength / 2 - 90) * Math.PI / 180;

        if (reverse) {
            const temp = startAngle;
            startAngle = endAngle;
            endAngle = temp;
        }

        const x1 = this.centerX + radius * Math.cos(startAngle);
        const y1 = this.centerY + radius * Math.sin(startAngle);
        const x2 = this.centerX + radius * Math.cos(endAngle);
        const y2 = this.centerY + radius * Math.sin(endAngle);

        const largeArcFlag = arcLength > 180 ? 1 : 0;
        const sweepFlag = reverse ? 0 : 1;

        return 'M ' + x1 + ' ' + y1 + ' A ' + radius + ' ' + radius + ' 0 ' +
               largeArcFlag + ' ' + sweepFlag + ' ' + x2 + ' ' + y2;
    },

    /**
     * Builds the SVG content for months wheel
     */
    buildMonthsWheel: function () {
        const pathsContainer = this.monthsSvg.querySelector('#month-paths');
        const textsContainer = this.monthTextsGroup;
        const angleStep = 360 / 12; // 30 degrees per month

        // Clear existing content
        pathsContainer.innerHTML = '';
        textsContainer.innerHTML = '';

        for (let i = 0; i < 12; i++) {
            const angle = i * angleStep;
            const pathId = 'month-path-' + i;

            // Create path for this month (arc to follow)
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('id', pathId);
            path.setAttribute('d', this.createArcPath(angle, this.radius, 25, false));
            path.setAttribute('fill', 'none');
            pathsContainer.appendChild(path);

            // Create text element with textPath
            const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            text.setAttribute('class', 'circular-date-picker__item circular-date-picker__item--month');
            text.setAttribute('data-value', i);
            text.setAttribute('text-anchor', 'middle');

            const textPath = document.createElementNS('http://www.w3.org/2000/svg', 'textPath');
            textPath.setAttribute('href', '#' + pathId);
            textPath.setAttribute('startOffset', '50%');
            textPath.textContent = this.monthNames[i];

            text.appendChild(textPath);
            textsContainer.appendChild(text);
        }
    },

    /**
     * Positions day items around the circle and updates day names based on current month
     */
    positionDaysWheel: function () {
        const year = this.selectedDate.getFullYear();
        const month = this.selectedDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const dayItems = this.daysCircle.querySelectorAll('.circular-date-picker__item--day');
        const angleStep = 360 / daysInMonth;

        for (let i = 0; i < dayItems.length; i++) {
            const item = dayItems[i];
            const day = i + 1;

            // Show/hide days based on month length
            if (day > daysInMonth) {
                item.style.display = 'none';
                continue;
            } else {
                item.style.display = '';
            }

            // Update day name
            const date = new Date(year, month, day);
            const dayOfWeek = date.getDay();
            const dayNameSpan = item.querySelector('.circular-date-picker__item-name');
            if (dayNameSpan) {
                dayNameSpan.textContent = this.dayNames[dayOfWeek];
            }

            // Position item around the circle
            // Start at -90° (top, 12 o'clock) and go clockwise
            const angle = ((day - 1) * angleStep) - 90;

            // Position at radius distance from center
            const radius = 920;
            const x = 50 + (radius * Math.cos(angle * Math.PI / 180)) / 1900 * 100;
            const y = 50 + (radius * Math.sin(angle * Math.PI / 180)) / 1900 * 100;

            item.style.left = x + '%';
            item.style.top = y + '%';

            // Set fixed rotation based on position - this never changes
            // Add 90deg offset so top item (at -90deg position) gets 0deg rotation (horizontal)
            item.style.transform = 'translate(-50%, -50%) rotate(' + (angle + 90) + 'deg)';
        }
    },

    /**
     * Calculates the rotation needed to position a month at the top
     * @param {number} monthIndex - 0-11
     * @returns {number} rotation in degrees
     */
    getRotationForMonth: function (monthIndex) {
        // Each month is 30 degrees (360/12)
        // Rotate counter-clockwise to bring the month to top (270°)
        return -monthIndex * 30;
    },

    /**
     * Calculates the rotation needed to position a day at the top
     * @param {number} dayIndex - 0 to daysInMonth-1
     * @returns {number} rotation in degrees
     */
    getRotationForDay: function (dayIndex) {
        const year = this.selectedDate.getFullYear();
        const month = this.selectedDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const angleStep = 360 / daysInMonth;
        return -dayIndex * angleStep;
    },

    /**
     * Updates the rotation of both wheels
     */
    updateRotation: function () {
        if (this.monthTextsGroup) {
            this.monthTextsGroup.setAttribute('transform', 'rotate(' + this.monthRotation + ' ' + this.centerX + ' ' + this.centerY + ')');
        }

        if (this.daysCircle) {
            this.daysCircle.style.transform = 'rotate(' + this.dayRotation + 'deg)';
        }
    },

    /**
     * Detects which item is at the top center and marks it as active
     */
    updateActiveItems: function () {
        // Update months
        const monthIndex = this.getActiveMonthIndex();
        const monthItems = this.monthTextsGroup.querySelectorAll('.circular-date-picker__item');
        monthItems.forEach((item, index) => {
            item.classList.remove('circular-date-picker__item--selected');
            if (index === monthIndex) {
                item.classList.add('circular-date-picker__item--selected');
            }
        });

        // Update days
        const dayIndex = this.getActiveDayIndex();
        const dayItems = this.daysCircle.querySelectorAll('.circular-date-picker__item');
        dayItems.forEach((item, index) => {
            item.classList.remove('circular-date-picker__item--selected');
            if (index === dayIndex) {
                item.classList.add('circular-date-picker__item--selected');
            }
        });

        // Update selected date if not dragging
        if (!this.isDragging) {
            const monthChanged = monthIndex !== this.selectedDate.getMonth();
            const dayNumberSpan = dayItems[dayIndex].querySelector('.circular-date-picker__item-number');
            const dayValue = parseInt(dayNumberSpan.textContent, 10);
            const dayChanged = dayValue !== this.selectedDate.getDate();

            if (monthChanged) {
                this.selectedDate.setMonth(monthIndex);
                this.positionDaysWheel();
                this.dayRotation = this.getRotationForDay(this.selectedDate.getDate() - 1);
                this.updateRotation();
                this.updateActiveItems();
                this.updateNativeInput();
            } else if (dayChanged) {
                this.selectedDate.setDate(dayValue);
                this.updateNativeInput();
            }
        }
    },

    /**
     * Gets the index of the month currently at the top
     */
    getActiveMonthIndex: function () {
        // Items are positioned starting at 270°, so we need to account for that offset
        // When rotation is 0°, item 0 is at top (270°)
        // When rotation is -30°, item 1 is at top
        const normalizedRotation = ((this.monthRotation % 360) + 360) % 360;
        const monthIndex = Math.round((360 - normalizedRotation) / 30) % 12;
        return monthIndex;
    },

    /**
     * Gets the index of the day currently at the top
     */
    getActiveDayIndex: function () {
        const year = this.selectedDate.getFullYear();
        const month = this.selectedDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const angleStep = 360 / daysInMonth;
        // Items are positioned starting at 270°, so we need to account for that offset
        const normalizedRotation = ((this.dayRotation % 360) + 360) % 360;
        const dayIndex = Math.round((360 - normalizedRotation) / angleStep) % daysInMonth;
        return dayIndex;
    },

    /**
     * Updates the native date input value
     */
    updateNativeInput: function () {
        const year = this.selectedDate.getFullYear();
        const month = (this.selectedDate.getMonth() + 1).toString().padStart(2, '0');
        const day = this.selectedDate.getDate().toString().padStart(2, '0');
        const dateString = year + '-' + month + '-' + day;

        this.nativeInput.value = dateString;
    },

    /**
     * Updates wheels from native input
     */
    updateFromNativeInput: function () {
        const dateValue = this.nativeInput.value;
        if (!dateValue) {
            return;
        }

        const dateParts = dateValue.split('-');
        if (dateParts.length !== 3) {
            return;
        }

        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const day = parseInt(dateParts[2], 10);

        this.selectedDate = new Date(year, month, day);
        this.positionDaysWheel();

        this.monthRotation = this.getRotationForMonth(month);
        this.dayRotation = this.getRotationForDay(day - 1);

        this.updateRotation();
        this.updateActiveItems();
    },

    /**
     * Handles drag start
     */
    handleStart: function (e) {
        const target = e.target.closest('[data-wheel]');
        if (!target) {
            return;
        }

        e.preventDefault();
        this.isDragging = true;
        this.dragTarget = target.dataset.wheel;

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        this.startX = clientX;
        this.lastX = clientX;
        this.lastTime = Date.now();
        this.velocity = 0;

        // Cancel any ongoing animation
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }

        // Remove animation class during drag
        if (this.dragTarget === 'months') {
            this.monthTextsGroup.classList.remove('circular-date-picker__svg-group--animating');
        } else {
            this.daysCircle.classList.remove('circular-date-picker__circle--animating');
        }

        document.body.classList.add('dragging-circular-picker');
    },

    /**
     * Handles drag move
     */
    handleMove: function (e) {
        if (!this.isDragging) {
            return;
        }
        e.preventDefault();

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const deltaX = clientX - this.lastX;
        const now = Date.now();
        const deltaTime = now - this.lastTime;

        // Convert pixel movement to rotation (degrees)
        // Sensitivity: 1 pixel = 0.3 degrees of rotation (inverted for correct direction)
        const rotationDelta = deltaX * 0.3;

        if (this.dragTarget === 'months') {
            this.monthRotation += rotationDelta;
        } else {
            this.dayRotation += rotationDelta;
        }

        this.updateRotation();

        // Calculate velocity for momentum (degrees per millisecond)
        if (deltaTime > 0) {
            const instantVelocity = rotationDelta / deltaTime;
            this.velocity = this.velocity * 0.8 + instantVelocity * 0.2;
        }

        this.lastX = clientX;
        this.lastTime = now;
    },

    /**
     * Handles drag end
     */
    handleEnd: function (e) {
        if (!this.isDragging) {
            return;
        }
        e.preventDefault();

        this.isDragging = false;
        document.body.classList.remove('dragging-circular-picker');

        // Apply momentum and snap
        this.applyMomentumAndSnap();
    },

    /**
     * Applies momentum scrolling and snaps to nearest item
     */
    applyMomentumAndSnap: function () {
        const wheelType = this.dragTarget;
        const currentRotation = wheelType === 'months' ? this.monthRotation : this.dayRotation;

        // Apply momentum (velocity is in degrees/ms, multiply by time factor)
        let targetRotation = currentRotation + this.velocity * 200;

        // Calculate snap angle
        let snapAngle;
        if (wheelType === 'months') {
            snapAngle = 30; // 360/12
        } else {
            const year = this.selectedDate.getFullYear();
            const month = this.selectedDate.getMonth();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            snapAngle = 360 / daysInMonth;
        }

        // Snap to nearest item
        targetRotation = Math.round(targetRotation / snapAngle) * snapAngle;

        // Animate to target
        this.animateToRotation(wheelType, targetRotation);
    },

    /**
     * Animates wheel to target rotation
     */
    animateToRotation: function (wheelType, targetRotation) {
        const startRotation = wheelType === 'months' ? this.monthRotation : this.dayRotation;
        const delta = targetRotation - startRotation;
        const duration = 300;
        let startTime = null;

        // Add animation class
        if (wheelType === 'months') {
            this.monthTextsGroup.classList.add('circular-date-picker__svg-group--animating');
        } else {
            this.daysCircle.classList.add('circular-date-picker__circle--animating');
        }

        const animate = (currentTime) => {
            if (startTime === null) startTime = currentTime;
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Ease out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            const currentRotation = startRotation + delta * eased;

            if (wheelType === 'months') {
                this.monthRotation = currentRotation;
            } else {
                this.dayRotation = currentRotation;
            }

            this.updateRotation();

            if (progress < 1) {
                this.animationId = requestAnimationFrame(animate);
            } else {
                this.animationId = null;
                if (wheelType === 'months') {
                    this.monthTextsGroup.classList.remove('circular-date-picker__svg-group--animating');
                } else {
                    this.daysCircle.classList.remove('circular-date-picker__circle--animating');
                }
                // Update selected date after animation completes
                this.updateActiveItems();
            }
        };

        this.animationId = requestAnimationFrame(animate);
    },

    /**
     * Binds event listeners
     */
    bindEvents: function () {
        // Mouse events
        this.monthsSvg.addEventListener('mousedown', this.handleStart.bind(this));
        this.daysCircle.addEventListener('mousedown', this.handleStart.bind(this));
        document.addEventListener('mousemove', this.handleMove.bind(this));
        document.addEventListener('mouseup', this.handleEnd.bind(this));

        // Touch events
        this.monthsSvg.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
        this.daysCircle.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
        document.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
        document.addEventListener('touchend', this.handleEnd.bind(this), { passive: false });

        // Native input change
        this.nativeInput.addEventListener('change', this.updateFromNativeInput.bind(this));

        // Prevent context menu
        this.monthsSvg.addEventListener('contextmenu', (e) => {
            e.preventDefault();
        });
        this.daysCircle.addEventListener('contextmenu', (e) => {
            e.preventDefault();
        });
    }
};
